import React, { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { Atmosphere } from '@/components/shared/Atmosphere'
import { Attribution } from '@/components/shared/Attribution'
import { DNA_PROFILES, DESTINATIONS } from '@/data/destinations'
import { generateFamilyDNASummary } from '@/services/ai'
import { getDestinationImages } from '@/services/images'
import { openDeal, getNetworkLabel, NETWORK_COLORS } from '@/services/affiliates'
import type { Destination, Deal, Img } from '@/types'

function ConfettiDot({ color, delay, x }: { color: string; delay: number; x: number }) {
  return (
    <div
      className="absolute rounded-full confetti-item pointer-events-none"
      style={{
        width: 6 + Math.random() * 8,
        height: 6 + Math.random() * 8,
        background: color,
        left: `${x}%`,
        top: -20,
        '--duration': `${2.2 + Math.random() * 1.5}s`,
        '--delay': `${delay}s`,
      } as React.CSSProperties}
    />
  )
}

const CONFETTI_COLORS = ['#FF6B2B', '#FF9500', '#FFD166', '#06D6A0', '#3DDBD9', '#7B2FBE', '#FF4D80']

// ─── Photo hero for a destination card — falls back to gradient+emoji ─────────
function DestPhotoHero({
  dest,
  images,
  size = 'small',
}: {
  dest: Destination
  images: Img[]
  size?: 'small' | 'large'
}) {
  const [failed, setFailed] = useState(false)
  const first = images[0]
  // Cinematic banner aspect for the large hero (matches parent ~2.2:1), compact for cards.
  const ratioStyle = size === 'large'
    ? { aspectRatio: '22 / 10' as const }
    : { aspectRatio: '16 / 9' as const }
  const showPhoto = !!first?.url && !failed

  return (
    <div className="relative overflow-hidden" style={{ ...ratioStyle, background: dest.gradient }}>
      {showPhoto ? (
        <img
          src={first.url}
          alt={first.alt || dest.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className={`w-full h-full flex items-center justify-center ${size === 'large' ? 'text-6xl' : 'text-4xl'}`}>
          {dest.emoji}
        </div>
      )}
    </div>
  )
}

export function DnaScreen() {
  const { dnaKey, answers, setSelectedDestination, setScreen } = useAppStore()
  const [selectedDest, setSelectedDest] = useState<Destination | null>(null)
  const [aiSummary, setAiSummary] = useState<string>('')
  const [aiLoading, setAiLoading] = useState(false)
  const [cardImages, setCardImages] = useState<Record<string, Img[]>>({})
  const [galleryImages, setGalleryImages] = useState<Img[]>([])
  const [confettiItems] = useState(() =>
    Array.from({ length: 20 }, (_, i) => ({
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      delay: Math.random() * 2,
      x: Math.random() * 100,
    }))
  )
  const expandedRef = useRef<HTMLDivElement>(null)

  const profile = DNA_PROFILES[dnaKey]
  const destinations = DESTINATIONS[dnaKey] ?? []

  // ── Select first destination on mount ──────────────────────────────────────
  useEffect(() => {
    if (destinations.length > 0) setSelectedDest(destinations[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dnaKey])

  // ── Fetch a thumbnail per destination card (graceful — [] on failure) ──────
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const entries = await Promise.all(
        destinations.map(async (d) => [d.id, await getDestinationImages(d, 1)] as const)
      )
      if (cancelled) return
      setCardImages(Object.fromEntries(entries))
    })()
    return () => {
      cancelled = true
    }
  }, [dnaKey])

  // ── Fetch the larger imagery gallery for the expanded destination ──────────
  useEffect(() => {
    if (!selectedDest) return
    let cancelled = false
    setGalleryImages([])
    ;(async () => {
      const imgs = await getDestinationImages(selectedDest, 4)
      if (!cancelled) setGalleryImages(imgs)
    })()
    return () => {
      cancelled = true
    }
  }, [selectedDest?.id])

  // ── Call Claude to personalise the DNA description ─────────────────────────
  useEffect(() => {
    if (!profile || answers.length === 0) return
    setAiLoading(true)
    const answerLabels = answers.map((a) => a.label)

    generateFamilyDNASummary(profile.name, answerLabels, destinations.map((d) => d.name))
      .then((summary) => {
        setAiSummary(summary)
        setAiLoading(false)
      })
      .catch(() => {
        // Fallback to static description — silently
        setAiLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dnaKey, answers.length])

  const handleSelectDest = (dest: Destination) => {
    setSelectedDest(dest)
    setTimeout(() => expandedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100)
  }

  const handleGoJourney = () => {
    if (selectedDest) {
      setSelectedDestination(selectedDest)
      setScreen('journey')
    }
  }

  if (!profile) return null

  // Use Claude's personalised summary if available, otherwise static
  const displayDescription = aiSummary || profile.description
  const heroImg = galleryImages[0]

  return (
    <div className="yonder-world min-h-screen flex flex-col">
      <Atmosphere density="low" />
      <Topbar backTo="quiz" />

      <div className="w-full max-w-3xl mx-auto">
      {/* DNA Hero */}
      <div className="relative z-10 px-5 pt-6 pb-5 overflow-hidden">
        {/* Confetti */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {confettiItems.map((c, i) => (
            <ConfettiDot key={i} {...c} />
          ))}
        </div>

        {/* Badge */}
        <div className="relative inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-sm rounded-full px-3 py-1 mb-3">
          <span className="text-[10px] font-black tracking-wider text-sun-pale uppercase">
            ✨ Your Family Travel DNA
          </span>
        </div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative font-black text-white text-3xl sm:text-4xl leading-tight mb-2"
          style={{ textShadow: '0 4px 30px rgba(255,149,0,0.3)' }}
        >
          You&rsquo;re a <span className="italic text-sun-pale">{profile.name}</span>!
        </motion.h1>

        {/* Claude-personalised description */}
        <div className="relative min-h-[48px]">
          {aiLoading ? (
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-sun-pale"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                  />
                ))}
              </div>
              <span className="text-[12px] text-white/60 font-semibold italic">
                Yonder AI is reading your family…
              </span>
            </div>
          ) : (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-[13px] font-semibold text-white/75 leading-relaxed max-w-md"
            >
              {displayDescription}
            </motion.p>
          )}
        </div>

        {/* Claude attribution pill */}
        {aiSummary && (
          <div className="relative mt-2 inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-2.5 py-1">
            <span className="text-[9px] font-black tracking-wide text-white/70 uppercase">
              ✦ Personalised by Claude AI
            </span>
          </div>
        )}

        {/* Answer chips */}
        <div className="relative flex flex-wrap gap-1.5 mt-3">
          {answers.map((a, i) => (
            <span
              key={i}
              className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-white/70"
            >
              {a.emoji} {a.label}
            </span>
          ))}
        </div>
      </div>

      {/* Destinations */}
      <div className="relative z-10 px-5 pt-4">
        <div className="text-[10px] font-black tracking-widest text-sun-pale/80 uppercase mb-3">
          🌍 Destinations matched to your family — tap any to explore
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-3">
          {destinations.map((dest) => (
            <motion.button
              key={dest.id}
              onClick={() => handleSelectDest(dest)}
              whileHover={{ y: -3, transition: { duration: 0.15 } }}
              whileTap={{ scale: 0.97 }}
              className={`rounded-2xl overflow-hidden text-left border-2 transition-all yonder-cream-card ${
                selectedDest?.id === dest.id
                  ? 'border-sun shadow-glow-sun'
                  : 'border-transparent hover:border-sun-light shadow-lg'
              }`}
            >
              <div className="relative">
                <DestPhotoHero dest={dest} images={cardImages[dest.id] ?? []} size="small" />
                <div className="absolute top-1.5 left-1.5 bg-white/90 rounded-md text-[9px] font-black px-1.5 py-0.5">
                  {dest.flag}
                </div>
                <div className="absolute top-1.5 right-1.5 bg-sun text-white rounded-md text-[9px] font-black px-1.5 py-0.5">
                  Save {dest.saving}
                </div>
              </div>
              <div className="p-2">
                {dest.country && (
                  <div className="text-[8px] font-black tracking-widest text-ink-3/70 uppercase mb-0.5 flex items-center gap-1">
                    <span>{dest.flag}</span>
                    <span>{dest.country}</span>
                  </div>
                )}
                <div className="text-sm font-black text-ink">{dest.name}</div>
                <div className="text-[10px] font-bold text-grass-light mt-0.5">{dest.ageRange}</div>
                <div className="text-[9px] font-bold text-sun mt-1 leading-tight truncate">
                  {dest.stops.slice(0, 3).map((s) => s.name.replace(/\s*[\p{Emoji}\u200d\uFE0F]+\s*$/gu, '')).join(' · ')}
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Expanded detail */}
        {selectedDest && (
          <motion.div
            ref={expandedRef}
            key={selectedDest.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl overflow-hidden yonder-cream-card shadow-cream-card mb-4"
          >
            {/* Larger photo hero */}
            <div className="relative">
              <DestPhotoHero dest={selectedDest} images={galleryImages} size="large" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/10 pointer-events-none" />
              <div className="absolute top-3 right-3 bg-sun text-white rounded-full text-xs font-black px-3 py-1">
                Save {selectedDest.saving}
              </div>
              <div className="absolute bottom-3 left-4 right-4">
                <div className="flex items-center gap-1.5 text-[10px] font-black tracking-widest text-white/80 uppercase">
                  <span>{selectedDest.flag}</span>
                  <span>{selectedDest.country ?? selectedDest.name}</span>
                  <span className="text-white/50">›</span>
                  <span className="text-sun-pale">{selectedDest.name}</span>
                </div>
                <div className="font-black text-white text-2xl leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                  {selectedDest.name}
                </div>
                <div className="text-[11px] font-semibold text-white/85">
                  {selectedDest.ageRange} · {selectedDest.description.substring(0, 45)}…
                </div>
                <div className="text-[10px] font-bold text-sun-pale/90 mt-1">
                  📍 {selectedDest.stops.slice(0, 4).map((s) => s.name.replace(/\s*[\p{Emoji}\u200d\uFE0F]+\s*$/gu, '')).join(' · ')}
                </div>
              </div>
              {heroImg && (
                <div className="absolute bottom-1 right-2">
                  <Attribution image={heroImg} />
                </div>
              )}
            </div>

            {/* Destination imagery gallery — 2x2 thumbnails */}
            {galleryImages.length > 0 && (
              <div className="px-4 pt-4">
                <div className="text-[9px] font-black tracking-widest text-ink-3 uppercase mb-2">
                  📷 Destination imagery
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {galleryImages.slice(0, 4).map((img, i) => (
                    <div key={i} className="relative rounded-xl overflow-hidden bg-sand" style={{ aspectRatio: '4 / 3' }}>
                      <img
                        src={img.thumb || img.url}
                        alt={img.alt || selectedDest.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          ;(e.currentTarget.parentElement as HTMLElement).style.display = 'none'
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Destination facts — 2x2 stat cards */}
            <div className="px-4 pt-4">
              <div className="text-[9px] font-black tracking-widest text-ink-3 uppercase mb-2">
                📊 Destination facts
              </div>
              <div className="grid grid-cols-2 gap-2">
                {selectedDest.facts.map((f, i) => (
                  <div key={i} className="rounded-xl border-2 border-[#F0EBE3] bg-sand/40 px-3 py-2.5">
                    <div className="text-lg font-black" style={{ color: profile.accentColor }}>{f.value}</div>
                    <div className="text-[9px] font-bold text-ink-3 mt-0.5">{f.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Why you'll love it */}
            <div className="px-4 pt-4">
              <div className="text-[9px] font-black tracking-widest text-ink-3 uppercase mb-2">
                💛 Why you&rsquo;ll love it
              </div>
              <ul className="flex flex-col gap-1.5">
                {selectedDest.loveIt.map((line, i) => (
                  <li key={i} className="flex items-start gap-2 text-[12px] font-semibold text-ink-2 leading-snug">
                    <span className="text-sun flex-shrink-0 mt-0.5">●</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick stats row (kept from original) */}
            <div className="grid grid-cols-3 border-y-2 border-[#F0EBE3] mt-4">
              {Object.entries(selectedDest.stats).map(([k, v]) => (
                <div key={k} className="px-3 py-2.5 border-r-2 border-[#F0EBE3] last:border-r-0">
                  <div className="text-[9px] font-black tracking-wider text-ink-3 uppercase mb-1">{k}</div>
                  <div className="text-lg font-black text-ink">{v}</div>
                </div>
              ))}
            </div>

            {/* Live partner deals — restyled as rows with network badge + save chip */}
            <div className="px-4 pt-4">
              <div className="text-[9px] font-black tracking-widest text-ink-3 uppercase mb-2">
                🎁 Live partner deals — unlocked for you
              </div>
              <div className="flex flex-col gap-2">
                {selectedDest.deals.map((deal: Deal, i: number) => (
                  <button
                    key={i}
                    onClick={() => openDeal(deal, selectedDest.name, profile.name)}
                    className="flex items-center gap-3 bg-white border-2 border-[#F0EBE3] rounded-2xl px-3 py-2.5 shadow-sm hover:border-sun hover:shadow-sun/15 hover:shadow-md active:scale-[0.98] transition-all text-left w-full group"
                    title={`Book via ${getNetworkLabel(deal.network)}`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-sand flex items-center justify-center text-lg flex-shrink-0">
                      {deal.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-black text-ink group-hover:text-sun transition-colors">{deal.title}</div>
                      <div className="text-[10px] font-semibold text-ink-3 mt-0.5">{deal.detail}</div>
                      <div
                        className="inline-block text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded mt-1"
                        style={{ background: NETWORK_COLORS[deal.network] + '18', color: NETWORK_COLORS[deal.network] }}
                      >
                        via {getNetworkLabel(deal.network)}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <div className="bg-sun-pale border-2 border-sun-light rounded-full text-[9px] font-black px-2.5 py-1 text-ink whitespace-nowrap">
                        {deal.saving}
                      </div>
                      <div className="text-[9px] font-black text-sun opacity-0 group-hover:opacity-100 transition-opacity">
                        Book now →
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleGoJourney}
              className="w-full py-3.5 mt-4 bg-gradient-to-r from-sun to-sun-light text-white font-black text-[15px] hover:from-sun-light hover:to-sun transition-all shadow-glow-sun"
            >
              🗺 See our family journey →
            </button>
          </motion.div>
        )}
      </div>
      </div>
    </div>
  )
}
